import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import { ButtonBase, Icon, Tooltip, Typography } from '@mui/material';
import {
  StoreUtil,
  PatientType,
  PatientTypes,
  LocationTypeUtil,
} from 'doctivity-shared/utils';
import { LoadingView } from './LoadingView';

const styles = (theme) => ({
  orgRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    height: 24,
    marginBottom: 4,
    color: '#2E93fA',
    fontSize: 12,
    fontWeight: 300,
    position: 'relative',
    width: '100%',
    whiteSpace: 'nowrap',
  },
  orgBar: {
    position: 'absolute',
    backgroundColor: '#2E93fA',
    transitionProperty: 'width',
    transitionDelay: '1s',
    transitionDuration: '1s',
    width: 0,
    height: 24,
    top: 0,
    left: 0,
    borderRadius: 2,
    zIndex: 1,
    overflow: 'hidden',
    color: '#ffffff',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  orgName: {
    display: 'flex',
    flexDirection: 'row',
    paddingTop: 2,
    gap: 8,
    zIndex: 0,
    maxWidth: '16rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  orgLongName: {
    maxWidth: '16rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  orgShortName: {
    maxWidth: '10rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  orgNameIcon: {
    fontSize: 10,
    paddingLeft: 6,
  },
  orgCount: {
    position: 'absolute',
    left: 396,
    fontSize: 11,
    fontWeight: 300,
    zIndex: 0,
  },
  orgNameBar: {
    zIndex: 2,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
    textOverflow: 'ellipsis',
  },
  orgCountBar: {
    position: 'absolute',
    left: 400,
    fontSize: 11,
    fontWeight: 300,
    zIndex: 3,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
  },
  orgCountBarShort: {
    position: 'absolute',
    fontSize: 11,
    fontWeight: 300,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
    left: 200,
  },
  infoTip: {
    fontSize: 13,
    fontWeight: 200,
  },
  colorKeyContainer: {
    minHeight: 40,
    gap: theme.spacing(1),
  },
  colorKey: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    fontSize: 12,
    fontWeight: 400,
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  colorKeyIcon: {
    width: 14,
    height: 14,
    marginRight: 2,
    borderRadius: 2,
  },
  colorKeyText: {
    marginRight: 12,
    whiteSpace: 'nowrap',
  },
  aggregatePercentage: {
    marginLeft: theme.spacing(1),
  },
  colorPagination: {
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    }
  }
});

function OrgProjectedPatientsChart({ classes, patientType, legend }) {
  const organizations = useSelector((state) =>
    StoreUtil.get(state.analytics, 'CLAIMS_PER_ORGANIZATION'),
  );
  const organizationCounts = useSelector((state) =>
    StoreUtil.get(state.analytics, 'CLAIMS_PER_ORGANIZATION_COUNTS'),
  );
  const [claimRows, setClaimRows] = useState([]);
  const [maxValue, setMaxValue] = useState(100);
  let isLoading = false;

  const generateClaimsCountBar = useCallback(
    (claims, index) => {
      let patientTypeLabel = 'patients';
      if (patientType === PatientType.CAPTURED_CLAIMS) {
        patientTypeLabel = 'claims';
      }

      return (
        <>
          {` ${
            claims.total ? claims.total.toLocaleString() : '<11'
          } ${patientTypeLabel}`}
          {claims.aggregateClaimsPercentage !== null &&
          claims.aggregateClaimsPercentage !== undefined ? (
            <span className={classes.aggregatePercentage}>
              {` ~${(claims.aggregateClaimsPercentage * 100).toLocaleString()}% ${
                index === 0 ? 'of Selected Market' : ''
              }`}
            </span>
          ) : (
            <></>
          )}
        </>
      );
    },
    [classes, patientType],
  );
  useEffect(() => {
    let max = 100;
    let rows = [];
    let totalMarket = 0;
    let affiliatedPercentage = -1;
    if (!organizations || !StoreUtil.isLoaded(organizations)) {
      isLoading = true;
    } else {
      rows = organizations.data.rows;
      if (organizationCounts?.data && organizationCounts.data.total) {
        totalMarket = organizationCounts.data.total;
        affiliatedPercentage = (
          (organizationCounts.data.total_affiliated /
            organizationCounts.data.total) *
          100
        ).toFixed(2);
      }
    }

    if (affiliatedPercentage > -1) {
      affiliatedPercentage = Math.round(affiliatedPercentage * 100) / 100;
    }

    rows.forEach((claims) => {
      if (claims.total === null) {
        claims.total = 0;
      } else {
        claims.total = parseInt(claims.total, 10);
        if (totalMarket) {
          claims.aggregateClaimsPercentage = claims.total / totalMarket;
        }
        if (
          claims.organization.is_affiliated ||
          claims.organization.affiliated?.length > 0
        ) {
          claims.affiliated = true;
        }
        max = Math.max(max, claims.total);
      }
      if (
        claims.organization &&
        claims.organization.name &&
        claims.organization.name.length > 50
      ) {
        claims.organization.name = `${claims.organization.name.substring(
          0,
          50,
        )}...`;
      }
    });
    setClaimRows(rows);
    setMaxValue(max);
  }, [
    StoreUtil.isLoaded(organizations),
    organizations?.data?.rows,
    organizationCounts?.data?.rows,
  ]);
  if (isLoading) return <LoadingView />;

  return (
    <>
      {(legend === undefined || legend) && patientType !== PatientType.CAPTURED_CLAIMS &&
 (
        <div className={classes.colorPagination}>
          <div className={`${classes.colorKey} ${classes.colorKeyContainer}`}>
            {LocationTypeUtil.getAll()
                .filter((type) => !type.codesOnly && type.id !== 0)
                .map((type) => {
                  return (
                    <div className={classes.colorKey} key={type.id}>
                      <div
                        className={classes.colorKeyIcon}
                        style={{
                          backgroundColor: type.color,
                        }}
                      />
                      <div className={classes.colorKeyText}>{type.name}</div>
                    </div>
                  );
                })}
          </div>
        </div>
      )}
      {claimRows.map((claims, index) => (
        <ButtonBase
          key={`claims:${claims.org_id}:${claims.location_type}:${index}`}
          className={classes.orgRow}
          style={{
            color: LocationTypeUtil.getColor(claims.location_type),
          }}
          href={`/organizations/${claims.organization.id}/market`}
        >
          <div
            className={classes.orgBar}
            style={{
              width: `${
                (claims.total && claims.total > 0
                  ? claims.total / maxValue
                  : 10 / maxValue) * 100
              }%`,
              backgroundColor: LocationTypeUtil.getColor(claims.location_type),
            }}
          >
            <div className={classes.orgNameBar}>
              {claims.affiliated ? (
                <Tooltip
                  placement='bottom-start'
                  title={
                    <Typography className={classes.infoTip}>
                      Affiliated Organization
                    </Typography>
                  }
                >
                  <Icon className={classes.orgNameIcon}>hub</Icon>
                </Tooltip>
              ) : (
                <div style={{ minWidth: '16px' }}></div>
              )}
              <span
                className={
                  legend === false ? classes.orgShortName : classes.orgLongName
                }
                style={{ overflow: 'clip' }}
              >
                {claims.organization.name}
              </span>
            </div>
            <div
              className={
                legend === false
                  ? classes.orgCountBarShort
                  : classes.orgCountBar
              }
              style={{
                textOverflow: 'initia',
                color: 'white',
              }}
            >
              {generateClaimsCountBar(claims, index)}
            </div>
          </div>
          <div className={classes.orgName}>
            {claims.affiliated ? (
              <Tooltip
                placement='bottom-start'
                title={
                  <Typography className={classes.infoTip}>
                    Affiliated Organization
                  </Typography>
                }
              >
                <Icon className={classes.orgNameIcon}>hub</Icon>
              </Tooltip>
            ) : (
              <div style={{ minWidth: '16px' }}></div>
            )}
            <span className={legend === false ? classes.orgShortName : ''}>
              {claims.organization.name}
            </span>
          </div>
          <div
            className={
              legend === false ? classes.orgCountBarShort : classes.orgCount
            }
            style={{
              color: LocationTypeUtil.getColor(claims.location_type),
            }}
          >
            {generateClaimsCountBar(claims, index)}
          </div>
        </ButtonBase>
      ))}
    </>
  );
}
OrgProjectedPatientsChart.propTypes = {
  classes: PropTypes.object,
  patientType: PropTypes.oneOf(PatientTypes).isRequired,
  legend: PropTypes.bool,
};
const Styled = withStyles(styles)(OrgProjectedPatientsChart);
export { Styled as OrgProjectedPatientsChart };
